import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserFollow } from '../../hooks/useUserFollow';

interface FollowButtonProps {
  targetUserId: string;
  onFollowChange?: (isFollowing: boolean) => void;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary';
  compact?: boolean;
}

export const FollowButton: React.FC<FollowButtonProps> = ({
  targetUserId,
  onFollowChange,
  size = 'md',
  variant = 'primary'
}) => {
  const { user } = useAuth();
  const { follow, unfollow, isFollowing } = useUserFollow(user?.id ?? '');
  const [following, setFollowing] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkFollowingStatus = async () => {
      if (user?.id && targetUserId && user.id !== targetUserId) {
        const status = await isFollowing(user.id, targetUserId);
        setFollowing(status);
      }
      setCheckingStatus(false);
    };
    checkFollowingStatus();
  }, [user?.id, targetUserId, isFollowing]);

  const handleFollowToggle = async () => {
    if (!user?.id || user.id === targetUserId) return;
    setLoading(true);
    
    try {
      if (following) {
        await unfollow(targetUserId);
      } else {
        await follow(targetUserId);
      }
      setFollowing(!following);
      onFollowChange?.(!following);
    } catch (error) {
      console.error('Erreur lors du follow/unfollow:', error);
      // Optionnel : afficher une notification d'erreur
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.id === targetUserId || checkingStatus) {
    return null;
  }

  const sizeClasses = {
    sm: 'px-3 py-1 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  const variantClasses = following
    ? 'bg-gray-600 hover:bg-red-600 text-white border border-gray-600 hover:border-red-600'
    : variant === 'primary'
    ? 'bg-blue-600 hover:bg-blue-700 text-white border border-blue-600'
    : 'bg-transparent hover:bg-blue-600 text-blue-400 hover:text-white border border-blue-400 hover:border-blue-600';

  return (
    <button
      onClick={handleFollowToggle}
      disabled={loading}
      className={`
        ${sizeClasses[size]}
        ${variantClasses}
        rounded-lg font-medium transition-all duration-200
        disabled:opacity-50 disabled:cursor-not-allowed
        flex items-center space-x-1
      `}
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : (
        <>
          {following ? (
            <>
              <span className="hidden group-hover:inline">Ne plus suivre</span>
              <span className="group-hover:hidden">Suivi</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Suivre</span>
            </>
          )}
        </>
      )}
    </button>
  );
};
